import { resolveBrandedDomainSurface } from "../lib/branded_domain_surface";
import { isSharedContinuityRoomProductHost } from "../lib/shared_continuity_room_hosts";

/**
 * Single source of truth for primary XO app routes shown in Sidebar + Flow site strip.
 * Routes are unchanged; only labels/presentation differ by surface.
 */

export type XONavItemId =
  | 'vault'
  | 'drops'
  | 'dashboard'
  | 'xo_reserve'
  | 'liquidity_radar'
  | 'eighth_seal'
  | 'message_bottle_v2'
  | 'claim'
  | 'traits'
  | 'digest'
  | 'signals'
  | 'signal_pass'
  | 'xo_call'
  | 'xo_call_open'
  | 'xo_call_dev'
  | 'xonvergence'
  | 'mission_control';

export type XONavItem = {
  id: XONavItemId;
  /** react-router `to` */
  path: string;
  /** Default label (Sidebar + fallback for flow strip) */
  label: string;
  /** Compact / explicit label for flow strip (e.g. "Eighth Seal" vs legacy "Drop") */
  flowLabel?: string;
  /** Prepended in Sidebar only (e.g. emoji) */
  sidebarPrefix?: string;
  /** Default: true */
  showInSidebar?: boolean;
  /** Default: true */
  showInFlowStrip?: boolean;
};

/**
 * Canonical order for both surfaces unless filtered.
 */
export const XO_NAV_ITEMS: readonly XONavItem[] = [
  /** Operator / product shells — reachable by URL; hidden from primary nav (product shell). */
  {
    id: 'mission_control',
    path: '/mission',
    label: 'Mission Control',
    flowLabel: 'Mission',
    sidebarPrefix: '◎ ',
    showInSidebar: false,
    showInFlowStrip: false,
  },
  /** Dev / ops dashboards — reachable by URL; hidden from primary nav (product shell). */
  { id: 'vault', path: '/vault', label: 'Vault', showInSidebar: false, showInFlowStrip: false },
  {
    id: 'dashboard',
    path: '/dashboard',
    label: 'Event Bus',
    showInSidebar: false,
    showInFlowStrip: false,
  },
  {
    id: 'xo_reserve',
    path: '/reserve',
    label: 'XO Reserve',
    sidebarPrefix: '🌌 ',
    showInSidebar: false,
    showInFlowStrip: false,
  },
  {
    id: 'xonvergence',
    path: '/xonvergence',
    label: 'XOnvergence',
    sidebarPrefix: '◇ ',
    showInSidebar: false,
    showInFlowStrip: false,
  },
  {
    id: 'liquidity_radar',
    path: '/liquidity-radar',
    label: 'Liquidity Radar',
    sidebarPrefix: '📡 ',
    showInSidebar: false,
    showInFlowStrip: false,
  },
  { id: 'drops', path: '/drops', label: 'Drops' },
  {
    id: 'eighth_seal',
    path: '/drops/eighth_seal',
    label: 'Drop',
    flowLabel: 'Eighth Seal',
    showInSidebar: false,
  },
  { id: 'message_bottle_v2', path: '/drops/message_bottle_v2', label: 'Message Bottle V2', flowLabel: 'Message Bottle V2', showInSidebar: false },
  { id: 'claim', path: '/drops/eighth_seal/claim', label: 'Claim', showInSidebar: false },
  { id: 'traits', path: '/traits', label: 'Trait Explorer', sidebarPrefix: '🧬 ' },
  { id: 'digest', path: '/digest', label: 'Digest' },
  { id: 'signals', path: '/drops/signals', label: 'Signals', flowLabel: 'Signals' },
  { id: 'signal_pass', path: '/signal-pass', label: 'Signal Pass', flowLabel: 'Signal Pass', showInSidebar: false },
  { id: 'xo_call', path: '/call/landing', label: 'XO Call', flowLabel: 'XO Call', sidebarPrefix: '📞 ', showInFlowStrip: false, showInSidebar: false },
  { id: 'xo_call_open', path: '/call', label: 'Open Call', flowLabel: 'Open Call', showInFlowStrip: false },
  { id: 'xo_call_dev', path: '/call/dev', label: 'Call (dev)', flowLabel: 'Call (dev)', sidebarPrefix: '⌁ ', showInFlowStrip: false, showInSidebar: false },
] as const;

export function navItemsForSidebar(): readonly XONavItem[] {
  return XO_NAV_ITEMS.filter((i) => i.showInSidebar !== false);
}

export function navItemsForFlowStrip(): readonly XONavItem[] {
  return XO_NAV_ITEMS.filter((i) => i.showInFlowStrip !== false);
}

export function navLabel(item: XONavItem, surface: 'sidebar' | 'flow'): string {
  if (surface === 'flow') return item.flowLabel ?? item.label;
  return item.label;
}

/** Message Bottle V2 uses Flow panel as primary nav; slim the global sidebar on this route. */
export function isMessageBottleV2Path(pathname: string): boolean {
  return pathname === '/drops/message_bottle_v2' || pathname.startsWith('/drops/message_bottle_v2/');
}


/** Reserve / XOnvergence / Signals — cinematic public surfaces (full-width, no sidebar chrome). */
export function isPublicInkSurfacePath(pathname: string): boolean {
  return isPublicExperiencePath(pathname);
}

/**
 * Public product surfaces: no sidebar; centered column shell in layout.
 * Intentionally narrow — dev dashboards stay off this path so the main flow stays premium.
 */
export function isPublicExperiencePath(pathname: string): boolean {
  if (pathname === '/' || pathname === '') return true;
  if (pathname === '/demo' || pathname.startsWith('/demo/')) return true;
  if (pathname === '/message-bottle' || pathname.startsWith('/message-bottle/')) return true;
  if (pathname === '/drops/message_bottle_v2' || pathname.startsWith('/drops/message_bottle_v2/')) return true;
  if (pathname === '/reserve' || pathname === '/xonvergence' || pathname === '/terminal') return true;
  if (pathname === '/pitch' || pathname.startsWith('/pitch/')) return true;
  if (pathname === '/harbor-walk' || pathname.startsWith('/harbor-walk/')) return true;
  if (pathname === '/harbor-seal-room' || pathname.startsWith('/harbor-seal-room/')) return true;
  if (pathname === '/harbor' || pathname.startsWith('/harbor/')) return true;
  if (pathname === '/moments' || pathname.startsWith('/moments/')) return true;
  if (pathname === '/my-harbor' || pathname.startsWith('/my-harbor/')) return true;
  if (pathname.startsWith('/aether')) return true;
  if (pathname.startsWith('/handoff')) return true;
  if (pathname.startsWith('/registrar')) return true;
  if (pathname.startsWith('/proof/')) return true;
  if (pathname === '/drops/signals' || pathname.startsWith('/drops/signals/')) return true;
  if (pathname === '/traits' || pathname.startsWith('/traits/')) return true;
  if (pathname === '/ledger' || pathname.startsWith('/ledger/')) return true;
  if (pathname === '/primer' || pathname.startsWith('/primer/')) return true;
  if (pathname === '/trust-continuum' || pathname.startsWith('/trust-continuum/')) return true;
  if (pathname === '/continuity-scene' || pathname.startsWith('/continuity-scene/')) return true;
  if (pathname === '/continuity-session' || pathname.startsWith('/continuity-session/')) return true;
  if (pathname === '/continuity-room' || pathname.startsWith('/continuity-room/')) return true;
  if (pathname === '/continuity-replay' || pathname.startsWith('/continuity-replay')) return true;
  if (pathname === '/trust-loop' || pathname.startsWith('/trust-loop/')) return true;
  if (pathname === '/constellation' || pathname.startsWith('/constellation/')) return true;
  if (pathname === '/artifacts' || pathname.startsWith('/artifacts/')) return true;
  if (pathname === '/mission' || pathname.startsWith('/mission/')) return true;
  if (pathname === '/xo-proof' || pathname.startsWith('/xo-proof/')) return true;
  if (pathname === '/record' || pathname.startsWith('/record/')) return true;
  if (pathname === '/pulse' || pathname.startsWith('/pulse/')) return true;
  if (pathname === '/call' || pathname.startsWith('/call/')) return true;
  if (pathname === '/call-continuity-demo') return true;
  return false;
}

/** True when hostname is a thin branded product domain — entire SPA is cinematic on that host. */
export function isBrandedProductDomainHost(hostname: string | undefined): boolean {
  if (!hostname) return false;
  return resolveBrandedDomainSurface(hostname) != null;
}

/** Full-bleed cinematic layout (no sidebar): product paths + Signal Pass (+ optional subpaths). */
export function isPublicCinematicShellPath(pathname: string, hostname?: string): boolean {
  if (hostname && isSharedContinuityRoomProductHost(hostname)) return true;

  if (hostname && isBrandedProductDomainHost(hostname)) return true;
  return isPublicExperiencePath(pathname) || pathname === '/signal-pass' || pathname.startsWith('/signal-pass/');
}
